'use client'

import type { TVesselJourneyResponse } from '@/api/v1/resources/vesselJourney'

export const BOOKMARKS_KEY = 'bookmarkedVessels'
const MAX_BOOKMARKS = 10

function canUseLocalStorage() {
  try {
    return typeof window !== 'undefined' && !!window.localStorage
  } catch {
    return false
  }
}

function readRaw(): TVesselJourneyResponse[] {
  if (!canUseLocalStorage()) return []
  try {
    const raw = window.localStorage.getItem(BOOKMARKS_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? (parsed as TVesselJourneyResponse[]) : []
  } catch (e) {
    console.warn('Failed to read bookmarks from localStorage, resetting key', e)
    try {
      window.localStorage.removeItem(BOOKMARKS_KEY)
    } catch {
      // Ignore errors clearing localStorage; bookmarks will be reset in memory only.
    }
    return []
  }
}

function writeRaw(list: TVesselJourneyResponse[]) {
  if (!canUseLocalStorage()) throw new Error('localStorage unavailable')
  const str = JSON.stringify(list)
  try {
    window.localStorage.setItem(BOOKMARKS_KEY, str)
  } catch (e) {
    // Try FIFO eviction once if quota exceeded
    if ((e as Error)?.name === 'QuotaExceededError' || (e as DOMException)?.code === 22) {
      const copy = [...list]
      copy.shift()
      const retryStr = JSON.stringify(copy)
      try {
        window.localStorage.setItem(BOOKMARKS_KEY, retryStr)
      } catch (e2) {
        console.error('Failed to persist bookmarks after eviction', e2)
        throw e2
      }
    } else {
      console.error('Failed to write bookmarks', e)
      throw e
    }
  }
}

export function getBookmarkedVessels(): TVesselJourneyResponse[] {
  return readRaw()
}

export function isVesselBookmarked(imo: string): boolean {
  const list = readRaw()
  return list.some((v) => v?.ship?.imo === imo)
}

export function toggleBookmark(vessel: TVesselJourneyResponse): {
  list: TVesselJourneyResponse[]
  bookmarked: boolean
} {
  if (!vessel?.ship?.imo) throw new Error('Invalid vessel object: missing ship.imo')
  const imo = vessel.ship.imo
  const current = readRaw()
  const exists = current.findIndex((v) => v?.ship?.imo === imo)
  let next: TVesselJourneyResponse[]
  let bookmarked: boolean

  if (exists >= 0) {
    // Remove existing
    next = current.filter((v) => v?.ship?.imo !== imo)
    bookmarked = false
  } else {
    next = [...current]
    // Enforce FIFO capacity
    if (next.length >= MAX_BOOKMARKS) {
      next.shift()
    }
    // Avoid duplicates by IMO
    next.push(vessel)
    bookmarked = true
  }

  writeRaw(next)
  return { list: next, bookmarked }
}
